import React, { useState, useEffect } from "react";
import { Stack, Typography, Box, Button, Sheet } from "@mui/joy";
import { useNavigate, useLocation } from "react-router-dom";
import { CodeInputGrid } from "../../../components/common/VerificationStep/CodeInputGrid";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { resendOTPThunk, verifyOTPThunk } from "../../../redux/applyWorkSlice";
import { ToastNotification } from "../../../components/common/ToastNotification";
import { OTP_ENABLED } from "../../../utils/otp";

interface LocationState {
  phoneNumber: string;
  fullName: string;
  companyName: string;
  redirectUrl?: string;
}

const PhoneVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  
  const { phoneNumber, fullName, companyName, redirectUrl } = (location.state as LocationState) || {};
  
  const [codeDigits, setCodeDigits] = useState(Array(6).fill(""));
  const [resendTimer, setResendTimer] = useState(60);
  const [showResend, setShowResend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    color: 'success' as 'success' | 'danger',
  });

  useEffect(() => {
    // Check if OTP is enabled
    if (!OTP_ENABLED) {
      // If OTP is disabled, redirect to the specified URL or dashboard
      const targetUrl = redirectUrl || '/dashboard';
      navigate(targetUrl, { 
        state: { 
          fullName, 
          companyName,
          phoneNumber 
        } 
      });
      return;
    }

    // Redirect if no phone number is provided
    if (!phoneNumber) {
      navigate('/auth/signup');
      return;
    }

    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setShowResend(true);
    }

    return () => clearInterval(timer);
  }, [resendTimer, phoneNumber, navigate, redirectUrl, fullName, companyName]);

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  const showErrorToast = (message: string) => {
    setToast({
      open: true,
      message,
      color: 'danger',
    });
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newDigits = [...codeDigits];
      newDigits[index] = value;
      setCodeDigits(newDigits);

      if (value !== "" && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleBackspace = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && codeDigits[index] === "" && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const formatPhoneNumber = (phone: string) => {
    if (phone.startsWith("+1") && phone.length === 12) {
      return `+1 *** **** * ${phone.slice(-2)}`;
    } else if (phone.length === 10) {
      return `(${phone.slice(0, 3)}) ***-** ${phone.slice(-2)}`;
    }
    return phone;
  };

  const isComplete = codeDigits.every((digit) => digit.length === 1);

  const onContinue = async () => {
    try {
      const otp = codeDigits.join("");
      await dispatch(verifyOTPThunk({ phoneNo: phoneNumber, otp })).unwrap();
      
      setToast({
        open: true,
        message: 'Phone number verified successfully!',
        color: 'success',
      });
      
      // Navigate to dashboard or success page after verification
      setTimeout(() => {
        navigate('/dashboard', { 
          state: { 
            fullName, 
            companyName,
            phoneNumber 
          } 
        });
      }, 1500);
      
    } catch (error) {
      console.error("Error during verification:", error);
      showErrorToast("Invalid verification code. Please try again.");
    }
  };

  const handleResendOTP = async () => {
    if (phoneNumber) {
      setIsLoading(true);
      try {
        await dispatch(resendOTPThunk({ phoneNo: phoneNumber })).unwrap();
        setResendTimer(60);
        setShowResend(false);
        
        setToast({
          open: true,
          message: 'Verification code resent successfully!',
          color: 'success',
        });
      } catch (err) {
        console.error("Failed to resend OTP:", err);
        showErrorToast("Failed to resend verification code. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f4f4',
        padding: '20px',
      }}
    >
      <Sheet
        sx={{
          width: '100%',
          maxWidth: '480px',
          padding: '40px',
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Stack
          spacing={4}
          alignItems="center"
          justifyContent="center"
          sx={{
            width: "100%",
          }}
        >
          <Stack
            spacing={1}
            alignItems="center"
            maxWidth={400}
            textAlign="center"
            width="100%"
          >
            <Typography 
              level="h1" 
              fontSize="28px" 
              sx={{ 
                fontWeight: 600, 
                color: '#1A1B1D',
                marginBottom: '8px'
              }}
            >
              Verify Your Phone
            </Typography>
            <Typography 
              level="description"
              sx={{ 
                fontSize: '16px',
                color: '#7B7F85',
                lineHeight: 1.5
              }}
            >
              We've sent a 6-digit verification code to{" "}
              <strong>{formatPhoneNumber(phoneNumber)}</strong>
            </Typography>
          </Stack>

          <CodeInputGrid
            codeDigits={codeDigits}
            onCodeChange={handleCodeChange}
            onBackspace={handleBackspace}
          />

          <Stack spacing={1} alignItems="center" textAlign="center">
            {showResend ? (
              <Typography
                level="body-sm"
                sx={{
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#2e2d7e",
                  textDecoration: `${!isLoading && "underline"}`,
                }}
                onClick={handleResendOTP}
              >
                {isLoading ? "Sending..." : "Resend Code"}
              </Typography>
            ) : (
              <Typography 
                level="label"
                sx={{ 
                  fontSize: '14px',
                  color: '#7B7F85'
                }}
              >
                Resend code in {formatTime(resendTimer)}
              </Typography>
            )}
          </Stack>

          <Box width="100%">
            <Button
              variant="solid"
              fullWidth
              onClick={onContinue}
              disabled={!isComplete}
              sx={{
                height: '56px',
                fontSize: '18px',
                fontWeight: 600,
                "&:hover": {
                  bgcolor: isComplete ? "#23225f" : "#2E2D7E4D",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#2e2d7e",
                  color: "#FFFFFF",
                  opacity: 0.3,
                },
              }}
            >
              Verify Phone Number
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              level="body-sm" 
              sx={{ 
                color: '#7B7F85', 
                fontSize: '16px',
                fontWeight: 400
              }}
            >
              Didn't receive the code?{' '}
              <Typography
                component="span"
                sx={{
                  color: '#2e2d7e',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => navigate('/auth/signup')}
              >
                Try a different number
              </Typography>
            </Typography>
          </Box>
        </Stack>

        <ToastNotification
          open={toast.open}
          message={toast.message}
          color={toast.color}
          onClose={handleCloseToast}
        />
      </Sheet>
    </Box>
  );
};

export default PhoneVerification;
